// SPA navigation helpers. Previously used to force a full reload when
// the URL pointed to a project but `root.data` didn't match — the
// reload re-ran the bootstrap in canvas/index.js. That shortcut made
// `workspace → project` feel like a refresh instead of an SPA jump.
// Now we fetch the project at runtime, rerun the same split + adapter
// pipeline, and swap `context.projectPages` + `root.data` in place.

import { deepClone } from 'smbls'
import { resolveProjectParams } from '../../../shared/functions/resolveProjectParams.js'
import { getProjectData } from '../../../shared/functions/fetchProject.js'
import { attachDbAdapters } from '../../../shared/functions/attachDbAdapters.js'
import { splitProjectForEditor } from '../../../shared/functions/splitProjectForEditor.js'

let _loadInFlight = null

export async function ensureProjectLoaded (el, s) {
  const { owner, key } = resolveProjectParams()
  const root = s.root || s
  const current = root?.data
  const currentKey = current?.key || current?.projectKey

  // URL has no project params — either a back-navigation to a known chrome
  // route (dashboard / workspace / etc.) or a dead unknown route.
  // Known chrome routes are handled by their own route entries in pages/index.js
  // so they never reach `main`. Any single-segment path that lands here is
  // an unknown route: show the 404 page instead of blank canvas chrome.
  if (!owner || !key) {
    const path = (typeof window !== 'undefined' ? window.location.pathname : '') || ''
    const segs = path.split('/').filter(Boolean)
    // A single unknown segment (e.g. /notFound, /foo) is a dead route.
    // Two or more segments without valid owner+key means the reserved-path
    // check fired — treat those as chrome (clear project state, no error).
    const isDeadRoute = segs.length === 1
    if (isDeadRoute) {
      root.update({
        data: null,
        projectNotFound: true,
        notFoundStatus: 404,
        notFoundTitle: 'Page not found',
        notFoundMessage: "The page you're looking for doesn't exist. It may have been moved or the URL may be incorrect."
      })
    } else {
      if (current && current.key) root.update({ data: null, projectNotFound: null })
    }
    return
  }

  // Already loaded and matches URL: nothing to do. Clear any prior
  // not-found flag since a retry-by-reload equivalent would reset it.
  if (currentKey && currentKey === key) {
    if (root.projectNotFound) root.update({ projectNotFound: null })
    return
  }

  // Dedupe parallel onRender invocations — DOMQL can re-render before
  // the first fetch resolves.
  const tag = `${owner}/${key}`
  if (_loadInFlight === tag) return
  _loadInFlight = tag

  try {
    const data = await getProjectData({ owner, key })
    if (_loadInFlight !== tag) return // URL changed mid-flight
    if (!data) {
      root.update({
        data: null,
        projectNotFound: true,
        notFoundStatus: 404,
        notFoundTitle: `${owner}/${key} not found`,
        notFoundMessage: "We couldn't find this project. It may have been moved, deleted, or not yet synced."
      })
      return
    }
    const project = attachDbAdapters(deepClone(data, { window }))
    const { projectPages } = splitProjectForEditor(project)
    // Mutate context.projectPages in place so downstream readers
    // (embed/Collection.js, functions/canvas/state.js) pick up the
    // new pages on their next read without needing a fresh context.
    if (el.context) el.context.projectPages = projectPages
    root.update({
      data: project,
      projectNotFound: null,
      notFoundStatus: null,
      notFoundTitle: null,
      notFoundMessage: null
    })
  } catch (err) {
    console.warn('[ensureProjectLoaded] fetch failed:', err?.message || err)
    if (_loadInFlight !== tag) return
    root.update({
      data: null,
      projectNotFound: true,
      notFoundStatus: 503,
      notFoundTitle: `Couldn't open ${owner}/${key}`,
      notFoundMessage: err?.message || 'Failed to load project'
    })
  } finally {
    if (_loadInFlight === tag) _loadInFlight = null
  }
}

// Escape hatch kept for explicit callers (e.g. a retry button in the
// not-found page). No runtime flow uses this anymore.
export function reloadPage () {
  if (typeof window !== 'undefined') window.location.reload()
}
